import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { authSelector, selectedOrganizationSelector } from "../../../../reducers/index.js";
import { useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import {
  organizationPropertyDelete,
  organizationPropertyGet,
  organizationPropertySet,
  organizationTestToken,
  procountorInitializeBenefitMappings,
  initializeCalendarEventMappings,
} from "../../../../api/index.js";
import { getIntegrations } from "../../../../actions/organization/activeintegrations.js";
import { Form, Image, Modal, Spinner, Tab, Tabs } from "react-bootstrap";
import { Confirmation } from "../../../../common/confirmation.jsx";
import { EventMapping } from "./EventMapping/EventMapping.jsx";
import { useNavigate } from "react-router";
import { hrEsalary, queryClient } from "../../../../main.jsx";
import { SettingsProcountorContextProvider } from "./SettingsProcountorContext.jsx";
import { BenefitMapping } from "./BenefitMapping/BenefitMapping.jsx";
import { EventMappingSave } from "./EventMapping/EventMappingSave.jsx";
import { BenefitMappingSave } from "./BenefitMapping/BenefitMappingSave.jsx";
import { Button } from "../../../../common/Button.tsx";

const key = "tokenProcountor";

export function SettingsProcountorModal({ setShow, active }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const auth = useSelector(authSelector);
  const selectedOrganization = useSelector(selectedOrganizationSelector);

  const { register, handleSubmit, setValue, watch } = useForm();

  const [testResult, setTestResult] = useState(<br />); // reserve space for test result
  const [activeTab, setActiveTab] = useState("apikey");

  // restore settings from JSON string
  async function fetchToken() {
    const res = await organizationPropertyGet(selectedOrganization.businessIdentifier ?? auth.companyBusinessId, key);
    if (res && res.status === 200) {
      for (const key in res.data) {
        setValue(key, res.data[key]);
      }
    }
  }

  // save settings as JSON string to organization's key-value store
  async function setToken(data) {
    const res = await organizationPropertySet(
      selectedOrganization.businessIdentifier ?? auth.companyBusinessId,
      key,
      JSON.stringify(data),
    );

    if (res && res.status === 200) {
      if (auth.companyBusinessId) {
        dispatch(getIntegrations(selectedOrganization.businessIdentifier ?? auth.companyBusinessId)); // update parent (updates active)
        initializeBenefitMappings();
        initializeEventMappings();
      } else {
        console.error("companyBusinessId null for", auth.companyNumber);
      }
    }
  }

  async function initializeBenefitMappings() {
    const res = await procountorInitializeBenefitMappings();

    if (res && res.status === 201) {
      queryClient.invalidateQueries({ queryKey: ["salaryMappings"] });
    }
  }

  async function initializeEventMappings() {
    const res = await initializeCalendarEventMappings();

    if (res && res.status === 201) {
      queryClient.invalidateQueries({ queryKey: ["mappings"] });
    }
  }

  async function deleteToken() {
    setTestResult(<br />);
    const res = await organizationPropertyDelete(
      selectedOrganization.businessIdentifier ?? auth.companyBusinessId,
      key,
    );
    if (res && res.status === 200) {
      for (const key in formValues) {
        setValue(key, "");
      }

      if (auth.companyBusinessId) {
        dispatch(getIntegrations(selectedOrganization.businessIdentifier ?? auth.companyBusinessId)); // update parent (updates active)
      } else {
        console.error("companyBusinessId null for", auth.companyNumber);
      }
    }
  }

  async function testToken() {
    setTestResult(<Spinner size="sm" variant="primary" />);
    try {
      const res = await organizationTestToken("procountor");
      if (res && res.status === 200) {
        const { data } = res;
        if (!data) {
          setTestResult(`${t("ApiError", "API error")} ⚡`);
        } else if (data.rights) {
          /*
"salaries": {
      "payroll": "ALL_RIGHTS",
      "personalSalariesOnly": false,
      "personRegister": "ALL_RIGHTS",
      "workingTimeTracking": "NOT_ENABLED",
      "personalWorkHourTrackingOnly": false
},
*/
          setTestResult(
            <>
              <p>{t("TokenValid", "Token is valid")} ✅</p>
              <pre style={{ maxHeight: 200 }}>{JSON.stringify(data.rights.salaries, null, 3)}</pre>
            </>,
          );
        } else {
          setTestResult(`${t("TokenInvalid", "Token is invalid")} ⛔`);
        }
      } else {
        setTestResult(<p>{t("ApiError", "API error")} ⚡</p>);
      }
    } catch (e) {
      const message = e.response?.data?.message;
      const msg = message ? JSON.parse(message).error_description : null; // Procountor error message description
      setTestResult(
        <>
          <p>{t("ApiError", "API error")} ⚡</p>
          <p style={{ color: "red" }}>{msg}</p>
        </>,
      );
    }
  }

  useEffect(() => {
    if (active) {
      fetchToken();
    }
  }, [active]);

  const closeModal = () => setShow();

  const formValues = watch();

  return (
    <Modal show onHide={closeModal} size="xl">
      <Form onSubmit={handleSubmit(setToken)} /* form must be inside modal to handle submit */>
        <Modal.Header closeButton>
          <Modal.Title style={{ lineHeight: 2 }}>
            <Image src="/integrations/procountor.svg" height={25} alt="finago procountor" />{" "}
            {t("integration", "integraatio")}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Tabs onSelect={(tab) => setActiveTab(tab)} activeKey={activeTab} defaultActiveKey="apikey" className="mb-3">
            <Tab eventKey="apikey" title={t("ApiKey")}>
              <div className="d-flex justify-content-center">
                <div style={{ width: "500px" }}>
                  {!active && (
                    <>
                      {location.hostname !== hrEsalary && (
                        <h1>
                          <a
                            onClick={() => navigate("/organization/details")}
                            target="_blank"
                            rel="noreferrer"
                            href="https://pts-secure.procountor.com/procountor/#!NAV_FRONT_PAGE/integrationid/MDJmNWUwN2UtMDA1Mi00YjFkLTkwNjYtNGFhNWI0ZmU5NzQx"
                          >
                            Easy API activation
                          </a>
                        </h1>
                      )}
                      <p>
                        {t(
                          "ProcountorApiInfo",
                          "Klikkaa ylläolevaa linkkiä tai luo manuaalisesti Procountorissa API-avain alla olevalla tunnisteella.",
                        )}
                      </p>
                      <p>
                        Asiakasohjelmiston tunniste (Client ID):
                        <br />
                        <b>{import.meta.env.VITE_PROCOUNTOR_CLIENT_ID}</b>
                      </p>
                      <p>
                        <a
                          href={import.meta.env.VITE_PROCOUNTOR_UI_URL + "/procountor/#!NAV_M2M_API_KEYS_MANAGEMENT"}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Luo uusi API-avain yllä olevalla tunnisteella, syötä se seuraavaan kenttään ja aktivoi
                          integraatio.
                        </a>
                      </p>
                    </>
                  )}
                  <Form.Group className="mb-3" controlId={"api_key"}>
                    <Form.Label>{t("ApiKey")}</Form.Label>
                    <Form.Control
                      {...register("api_key")}
                      readOnly={active}
                      type={active ? "password" : "text"}
                      onFocus={active ? (e) => e.target.select() : null}
                      style={{ userSelect: active ? "all" : "initial" }}
                    />
                  </Form.Group>
                  {testResult}
                </div>
              </div>
            </Tab>
            {active &&
              <Tab eventKey="event" title={t("EventMapping")}>
                <EventMapping integration="PROCOUNTOR" />
              </Tab>
            }
            {active &&
              <Tab eventKey="benefit" title={t("BenefitMapping")}>
                <BenefitMapping />
              </Tab>
            }
          </Tabs>
        </Modal.Body>

        <Modal.Footer>
          {activeTab === "apikey" && (
            <>
              {active && (
                <Button variant="primary" onClick={testToken}>
                  {t("TestConnection", "Testaa yhteys")}
                </Button>
              )}
              <Confirmation
                title={
                  active
                    ? t(
                      "NoteDeactivate",
                      "API-avain poistetaan ja pyynnöt rajapintaan vanhalla avaimella ei enää toimi. Haluatko poistaa integraation käytöstä",
                    )
                    : t("NoteActivate", "Haluatko määrittää uuden API-avaimen")
                }
                actionFunction={active ? deleteToken : handleSubmit(setToken)}
              >
                <Button
                  key="key"
                  variant={active ? "error" : "success"}
                  disabled={formValues?.api_key === undefined || formValues.api_key?.trim().length < 1}
                >
                  {active ? t("Deactivate", "Poista käytöstä") : t("Activate", "Ota käyttöön")}
                </Button>
              </Confirmation>
            </>
          )}
          {activeTab === "event" && <EventMappingSave closeModal={closeModal} />}
          {activeTab === "benefit" && <BenefitMappingSave closeModal={closeModal} />}
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export const SettingsProcountor = ({ active, setShow }) => (
  <SettingsProcountorContextProvider>
    <SettingsProcountorModal active={active} setShow={setShow} />
  </SettingsProcountorContextProvider>
);
